/**
 * /mi-dia — Panel "Mi Día" — la pantalla que abres al empezar la jornada.
 *
 * Widgets accionables: cada uno con su contador + lista corta + CTA "Ir a…".
 * Datos cargados en 1 sola llamada (GET /api/mi-dia/dashboard).
 * Refresco cada 60s.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import {
  PackageCheck, CheckSquare, ClipboardCheck, FileStack, Truck, Receipt,
  Target, Cake, MessageCircle, ArrowRight, RefreshCw, AlertCircle, Loader2,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";

function eur(n) {
  return (Number(n) || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit" });
  } catch { return iso; }
}

function relTime(iso) {
  if (!iso) return "";
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `hace ${diff}s`;
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  return `hace ${Math.floor(diff / 86400)} d`;
}

const ESTADO_RUTA_COLORS = {
  planificada: "border-amber-500/40 text-amber-300 bg-amber-500/10",
  en_curso: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
  finalizada: "border-zinc-700 text-zinc-400 bg-zinc-800/40",
};

export default function MiDia() {
  const { user, hasRole } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isAdmin = hasRole?.("admin");
  const isComercial = hasRole?.("comercial");
  const isMontador = hasRole?.("montador");

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data: r } = await api.get("/mi-dia/dashboard");
      setData(r);
    } catch (err) {
      if (!silent) toast.error("Error cargando Mi Día");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(() => load(true), 60_000);
    return () => clearInterval(t);
  }, []);

  if (loading && !data) {
    return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;
  }

  if (!data) return null;

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 6) return "Buenas noches";
    if (h < 13) return "Buenos días";
    if (h < 21) return "Buenas tardes";
    return "Buenas noches";
  })();

  return (
    <div className="space-y-6" data-testid="mi-dia-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-zinc-50 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-amber-400" />
            {greeting}, {user?.name?.split(" ")[0] || "DNF"}
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            Aquí tienes lo que más necesita tu atención hoy · Actualizado {relTime(data.generated_at)}
          </p>
        </div>
        <Button onClick={() => load()} size="sm" variant="outline" data-testid="mi-dia-refresh">
          <RefreshCw className="w-3.5 h-3.5 mr-2" /> Refrescar
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {/* Componentes recibidos SIN fecha de montaje (P1 destacado) */}
        {(isAdmin || isMontador) && (
          <WidgetCard
            title="Componentes en nave sin montaje"
            subtitle="Recibidos pero NO asignados a ninguna ruta"
            icon={PackageCheck}
            color="amber"
            count={data.componentes_sin_montaje?.count || 0}
            priority="alta"
            ctaTo="/pendientes-montar"
            ctaLabel="Asignar a ruta"
            testid="widget-componentes-sin-montaje"
          >
            <ItemList
              items={data.componentes_sin_montaje?.items || []}
              empty="¡Todos los componentes asignados!"
              render={(c) => (
                <Link to={`/peticiones/${c.peticion_id}`} key={c.id}
                  className="flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-zinc-800/60 text-sm">
                  <div className="flex-1 min-w-0">
                    <div className="text-zinc-200 truncate">
                      {c.titulo || "Componente sin título"}
                      {c.codigo_corto && <span className="ml-2 text-[10px] font-mono text-amber-300">[{c.codigo_corto}]</span>}
                    </div>
                    <div className="text-[10px] text-zinc-500">{relTime(c.updated_at)}</div>
                  </div>
                  <ArrowRight className="w-3.5 h-3.5 text-zinc-500" />
                </Link>
              )}
            />
          </WidgetCard>
        )}

        {/* Mis tareas */}
        <WidgetCard
          title="Mis tareas"
          subtitle={`${data.mis_tareas?.vencidas_count || 0} vencidas · esta semana`}
          icon={CheckSquare}
          color={data.mis_tareas?.vencidas_count > 0 ? "red" : "blue"}
          count={data.mis_tareas?.count || 0}
          ctaTo="/tareas"
          ctaLabel="Ver todas"
          testid="widget-mis-tareas"
        >
          <ItemList
            items={data.mis_tareas?.items || []}
            empty="Sin tareas pendientes esta semana"
            render={(t) => {
              const venc = t.fecha_limite && t.fecha_limite < new Date().toISOString().slice(0, 10);
              return (
                <Link to={`/tareas`} key={t.id}
                  className="flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-zinc-800/60 text-sm">
                  <div className="flex-1 min-w-0">
                    <div className="text-zinc-200 truncate">{t.titulo}</div>
                    <div className="text-[10px] flex gap-1.5">
                      <span className={venc ? "text-red-400" : "text-zinc-500"}>
                        {venc ? "VENCIDA · " : ""}{fmtDate(t.fecha_limite)}
                      </span>
                      {t.prioridad === "alta" && <span className="text-amber-400">alta</span>}
                    </div>
                  </div>
                </Link>
              );
            }}
          />
        </WidgetCard>

        {/* Reportes pendientes (admin) */}
        {isAdmin && (
          <WidgetCard
            title="Reportes pendientes"
            subtitle="Sin validar por administración"
            icon={ClipboardCheck}
            color="violet"
            count={data.reportes_pendientes?.count || 0}
            ctaTo="/reportes"
            ctaLabel="Validar"
            testid="widget-reportes-pendientes"
          >
            <ItemList
              items={data.reportes_pendientes?.items || []}
              empty="¡Todos los reportes validados!"
              render={(r) => (
                <Link to={`/reportes`} key={r.parada_id}
                  className="flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-zinc-800/60 text-sm">
                  <div className="flex-1 min-w-0">
                    <div className="text-zinc-200 truncate">
                      {r.cliente_nombre} · {r.espacio_nombre}
                    </div>
                    <div className="text-[10px] flex gap-1.5">
                      <span className="text-zinc-500 font-mono">{r.ruta_codigo}</span>
                      <span className={r.cliente_validado ? "text-emerald-400" : "text-red-400"}>
                        {r.cliente_validado ? "✓ cliente" : "✗ cliente"}
                      </span>
                      <span className={r.albaran_estado === "ok" ? "text-emerald-400" : "text-red-400"}>
                        {r.albaran_estado === "ok" ? "✓ albarán" : "✗ albarán"}
                      </span>
                    </div>
                  </div>
                </Link>
              )}
            />
          </WidgetCard>
        )}

        {/* Peticiones nuevas */}
        {(isAdmin || isComercial) && (
          <WidgetCard
            title="Peticiones nuevas (24h)"
            subtitle="Pendientes de asignar a producción"
            icon={FileStack}
            color="emerald"
            count={data.peticiones_nuevas?.count || 0}
            ctaTo="/peticiones"
            ctaLabel="Ver peticiones"
            testid="widget-peticiones-nuevas"
          >
            <ItemList
              items={data.peticiones_nuevas?.items || []}
              empty="Sin peticiones nuevas en las últimas 24h"
              render={(p) => (
                <Link to={`/peticiones/${p.id}`} key={p.id}
                  className="flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-zinc-800/60 text-sm">
                  <div className="flex-1 min-w-0">
                    <div className="text-zinc-200 truncate">
                      <span className="font-mono text-[10px] text-emerald-300 mr-1">{p.codigo}</span>
                      {p.titulo}
                    </div>
                    <div className="text-[10px] text-zinc-500">{p.cliente_nombre} · {relTime(p.fecha_creacion)}</div>
                  </div>
                </Link>
              )}
            />
          </WidgetCard>
        )}

        {/* Rutas de hoy */}
        <WidgetCard
          title="Rutas de hoy"
          subtitle={new Date().toLocaleDateString("es-ES", { weekday: "long", day: "2-digit", month: "long" })}
          icon={Truck}
          color="blue"
          count={data.rutas_hoy?.count || 0}
          ctaTo="/cronograma-rutas"
          ctaLabel="Cronograma"
          testid="widget-rutas-hoy"
        >
          <ItemList
            items={data.rutas_hoy?.items || []}
            empty="Sin rutas planificadas hoy"
            render={(r) => (
              <Link to={`/rutas/${r.id}`} key={r.id}
                className="flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-zinc-800/60 text-sm">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[11px] text-blue-300">{r.codigo}</span>
                    <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded border ${ESTADO_RUTA_COLORS[r.estado] || ""}`}>
                      {r.estado}
                    </span>
                  </div>
                  <div className="text-[10px] text-zinc-500 truncate">
                    {(r.montadores || []).slice(0, 2).join(", ") || "Sin asignar"}
                    {r.vehiculo_matricula && ` · ${r.vehiculo_matricula}`}
                    {r.paradas_count > 0 && ` · ${r.paradas_count} paradas`}
                  </div>
                </div>
              </Link>
            )}
          />
        </WidgetCard>

        {/* Facturas vencidas */}
        {isAdmin && (
          <WidgetCard
            title="Facturas vencidas"
            subtitle={data.facturas_vencidas?.importe_total > 0 ? `Total: ${eur(data.facturas_vencidas.importe_total)}` : "Sin facturas vencidas"}
            icon={Receipt}
            color="red"
            count={data.facturas_vencidas?.count || 0}
            ctaTo="/facturas?estado=vencida"
            ctaLabel="Gestionar cobros"
            testid="widget-facturas-vencidas"
          >
            <ItemList
              items={data.facturas_vencidas?.items || []}
              empty="¡Sin facturas vencidas! 🎉"
              render={(f) => (
                <Link to={`/facturas`} key={f.id}
                  className="flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-zinc-800/60 text-sm">
                  <div className="flex-1 min-w-0">
                    <div className="text-zinc-200 truncate">{f.cliente_nombre}</div>
                    <div className="text-[10px] text-zinc-500">
                      <span className="font-mono">{f.numero}</span> · vence {fmtDate(f.fecha_vencimiento)}
                    </div>
                  </div>
                  <div className="text-sm font-mono text-red-300">{eur(f.importe_total)}</div>
                </Link>
              )}
            />
          </WidgetCard>
        )}

        {/* Leads inactivos */}
        {(isAdmin || isComercial) && (
          <WidgetCard
            title="Leads inactivos"
            subtitle="Sin contacto +7 días"
            icon={Target}
            color="amber"
            count={data.leads_inactivos?.count || 0}
            ctaTo="/leads"
            ctaLabel="Ver leads"
            testid="widget-leads-inactivos"
          >
            <ItemList
              items={data.leads_inactivos?.items || []}
              empty="Todos los leads recientes"
              render={(l) => (
                <Link to={`/leads/${l.id}`} key={l.id}
                  className="flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-zinc-800/60 text-sm">
                  <div className="flex-1 min-w-0">
                    <div className="text-zinc-200 truncate">{l.empresa || l.nombre}</div>
                    <div className="text-[10px] text-zinc-500">
                      {l.estado} · último contacto {relTime(l.ultimo_contacto_at) || "nunca"}
                    </div>
                  </div>
                </Link>
              )}
            />
          </WidgetCard>
        )}

        {/* Cumpleaños */}
        {data.cumples?.count > 0 && (
          <WidgetCard
            title="Cumpleaños"
            subtitle="Equipo DNF"
            icon={Cake}
            color="pink"
            count={data.cumples.count}
            testid="widget-cumples"
          >
            <ItemList
              items={data.cumples.items || []}
              render={(u) => (
                <div key={u.id} className="flex items-center justify-between gap-2 px-2 py-1.5 text-sm">
                  <span className="text-zinc-200">🎂 {u.name}</span>
                  <Badge variant="outline" className="text-xs">
                    {u.cuando}
                  </Badge>
                </div>
              )}
            />
          </WidgetCard>
        )}

        {/* WhatsApp sin responder */}
        {(isAdmin || isComercial) && (
          <WidgetCard
            title="WhatsApp sin responder"
            subtitle="Clientes esperando respuesta +2h"
            icon={MessageCircle}
            color={data.whatsapp_sin_responder?.count > 0 ? "amber" : "zinc"}
            count={data.whatsapp_sin_responder?.count || 0}
            ctaTo="/whatsapp"
            ctaLabel="Abrir bandeja"
            testid="widget-whatsapp-sin-responder"
          >
            <ItemList
              items={data.whatsapp_sin_responder?.items || []}
              empty="Todo respondido 👍"
              render={(c) => (
                <Link to={`/whatsapp`} key={c.id}
                  className="flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-zinc-800/60 text-sm">
                  <div className="flex-1 min-w-0">
                    <div className="text-zinc-200 truncate">
                      {c.contact_name || c.contact_profile_name || `+${c.contact_wa_id}`}
                    </div>
                    <div className="text-[10px] text-zinc-500 truncate">
                      {relTime(c.last_inbound_at)} · {c.last_message_preview}
                    </div>
                  </div>
                  {c.unread_count > 0 && (
                    <Badge className="bg-amber-500 text-zinc-900 text-[10px]">{c.unread_count}</Badge>
                  )}
                </Link>
              )}
            />
          </WidgetCard>
        )}
      </div>
    </div>
  );
}

// ---------- Helpers ----------

const COLOR_CLASSES = {
  amber: { border: "border-amber-500/30", icon: "text-amber-400", num: "text-amber-300", bg: "bg-amber-500/5" },
  red: { border: "border-red-500/30", icon: "text-red-400", num: "text-red-300", bg: "bg-red-500/5" },
  blue: { border: "border-blue-500/30", icon: "text-blue-400", num: "text-blue-300", bg: "bg-blue-500/5" },
  emerald: { border: "border-emerald-500/30", icon: "text-emerald-400", num: "text-emerald-300", bg: "bg-emerald-500/5" },
  violet: { border: "border-violet-500/30", icon: "text-violet-400", num: "text-violet-300", bg: "bg-violet-500/5" },
  pink: { border: "border-pink-500/30", icon: "text-pink-400", num: "text-pink-300", bg: "bg-pink-500/5" },
  zinc: { border: "border-zinc-700", icon: "text-zinc-400", num: "text-zinc-200", bg: "bg-zinc-900/40" },
};

function WidgetCard({ title, subtitle, icon: Icon, color = "zinc", count, priority, ctaTo, ctaLabel, children, testid }) {
  const c = COLOR_CLASSES[color] || COLOR_CLASSES.zinc;
  return (
    <div className={`rounded-xl border ${c.border} ${c.bg} backdrop-blur-sm overflow-hidden flex flex-col`} data-testid={testid}>
      <div className="p-4 border-b border-zinc-800/40">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <Icon className={`w-4 h-4 ${c.icon}`} />
            <div>
              <div className="text-sm font-semibold text-zinc-100">{title}</div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">{subtitle}</div>
            </div>
          </div>
          <div className={`text-3xl font-bold ${c.num} font-mono leading-none`}>{count}</div>
        </div>
        {priority === "alta" && count > 0 && (
          <div className="mt-2 text-[10px] text-amber-300 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" /> Alta prioridad — acciónalo hoy
          </div>
        )}
      </div>
      <div className="flex-1 p-2 space-y-0.5 max-h-80 overflow-y-auto">
        {children}
      </div>
      {ctaTo && (
        <Link to={ctaTo}
          className="px-4 py-2 border-t border-zinc-800/40 text-xs text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/40 flex items-center justify-between transition-colors"
          data-testid={`${testid}-cta`}>
          <span>{ctaLabel}</span>
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      )}
    </div>
  );
}

function ItemList({ items, render, empty = "Sin elementos" }) {
  if (!items || items.length === 0) {
    return <div className="text-center py-4 text-xs text-zinc-500">{empty}</div>;
  }
  return <>{items.slice(0, 6).map(render)}</>;
}
